export interface StackedProgressCounts {
  readonly complete: number;
  readonly inProgress: number;
  readonly needsHelp: number;
  readonly notStarted: number;
}

/**
 * Bare four-segment stacked track (no label/percent chrome). Reused by
 * StackedProgressBar and the cohort-pulse rows so segment styling stays in sync.
 */
export function StackedProgressTrack({
  complete,
  inProgress,
  needsHelp,
  notStarted,
  label,
  className,
  emptyLabel = "No data yet",
}: StackedProgressCounts & {
  readonly label: string;
  readonly className?: string;
  readonly emptyLabel?: string;
}) {
  const total = complete + inProgress + needsHelp + notStarted;
  if (total <= 0) {
    return (
      <div
        className={["submissions-stack-track submissions-stack-track--empty", className]
          .filter(Boolean)
          .join(" ")}
      >
        <span className="muted small">{emptyLabel}</span>
      </div>
    );
  }
  return (
    <div
      className={["submissions-stack-track", className].filter(Boolean).join(" ")}
      role="img"
      aria-label={`${label}: ${complete} complete, ${inProgress} in progress, ${needsHelp} needs help, ${notStarted} not started`}
    >
      <span
        className="submissions-stack-seg submissions-stack-seg--complete"
        style={{ width: `${(complete / total) * 100}%` }}
      />
      <span
        className="submissions-stack-seg submissions-stack-seg--in-progress"
        style={{ width: `${(inProgress / total) * 100}%` }}
      />
      <span
        className="submissions-stack-seg submissions-stack-seg--needs-help"
        style={{ width: `${(needsHelp / total) * 100}%` }}
      />
      <span
        className="submissions-stack-seg submissions-stack-seg--not-started"
        style={{ width: `${(notStarted / total) * 100}%` }}
      />
    </div>
  );
}

export interface StackedProgressBarProps extends StackedProgressCounts {
  readonly label: string;
}

/**
 * Labeled stacked progress bar (label · track · % complete). Shared by the
 * teacher analytics dashboard and the public showcase.
 */
export default function StackedProgressBar({
  complete,
  inProgress,
  needsHelp,
  notStarted,
  label,
}: StackedProgressBarProps) {
  const total = complete + inProgress + needsHelp + notStarted;
  if (total <= 0) {
    return (
      <div className="submissions-stack-row">
        <span className="submissions-stack-label">{label}</span>
        <div className="submissions-stack-track submissions-stack-track--empty">
          <span className="muted small">No data yet</span>
        </div>
      </div>
    );
  }
  return (
    <div className="submissions-stack-row">
      <span className="submissions-stack-label" title={label}>
        {label}
      </span>
      <StackedProgressTrack
        complete={complete}
        inProgress={inProgress}
        needsHelp={needsHelp}
        notStarted={notStarted}
        label={label}
      />
      <span className="submissions-stack-pct">{Math.round((complete / total) * 100)}%</span>
    </div>
  );
}
